import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

/**
 * Feedback Store
 *
 * Manages persistent feedback state for chat messages.
 * Tracks which messages have received thumbs up/down feedback
 * to show the correct icon state even after page refresh.
 *
 * Dependencies:
 * - None (standalone store)
 *
 * Cleared by:
 * - useAuth hook during logout (via clearState)
 */

interface FeedbackState {
  // Map of conversationId -> messageIndex -> feedback type
  feedbackMap: Record<string, Record<number, 'thumbsUp' | 'thumbsDown'>>;
}

interface FeedbackActions {
  // Set feedback for a specific message
  setFeedback: (
    conversationId: string,
    messageIndex: number,
    feedback: 'thumbsUp' | 'thumbsDown'
  ) => void;

  // Get feedback for a specific message
  getFeedback: (
    conversationId: string,
    messageIndex: number
  ) => 'thumbsUp' | 'thumbsDown' | null;

  // Check if a message has thumbs up feedback
  hasThumbsUp: (conversationId: string, messageIndex: number) => boolean;

  // Check if a message has thumbs down feedback
  hasThumbsDown: (conversationId: string, messageIndex: number) => boolean;

  // Clear feedback for a specific message
  clearFeedback: (conversationId: string, messageIndex: number) => void;

  // Clear all feedback for a conversation
  clearConversationFeedback: (conversationId: string) => void;

  // Clear all state
  clearState: () => void;
}

const initialState: FeedbackState = {
  feedbackMap: {},
};

export const useFeedbackStore = create<FeedbackState & FeedbackActions>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        setFeedback: (conversationId, messageIndex, feedback) => {
          set(
            state => {
              const newFeedbackMap = { ...state.feedbackMap };
              if (!newFeedbackMap[conversationId]) {
                newFeedbackMap[conversationId] = {};
              }
              newFeedbackMap[conversationId][messageIndex] = feedback;
              return { feedbackMap: newFeedbackMap };
            },
            false,
            'feedback/setFeedback'
          );
        },

        getFeedback: (conversationId, messageIndex) => {
          const state = get();
          return state.feedbackMap[conversationId]?.[messageIndex] || null;
        },

        hasThumbsUp: (conversationId, messageIndex) => {
          const state = get();
          return (
            state.feedbackMap[conversationId]?.[messageIndex] === 'thumbsUp'
          );
        },

        hasThumbsDown: (conversationId, messageIndex) => {
          const state = get();
          return (
            state.feedbackMap[conversationId]?.[messageIndex] === 'thumbsDown'
          );
        },

        clearFeedback: (conversationId, messageIndex) => {
          set(
            state => {
              const newFeedbackMap = { ...state.feedbackMap };
              if (newFeedbackMap[conversationId]) {
                const newConversationMap = {
                  ...newFeedbackMap[conversationId],
                };
                delete newConversationMap[messageIndex];
                newFeedbackMap[conversationId] = newConversationMap;
              }
              return { feedbackMap: newFeedbackMap };
            },
            false,
            'feedback/clearFeedback'
          );
        },

        clearConversationFeedback: conversationId => {
          set(
            state => {
              const newFeedbackMap = { ...state.feedbackMap };
              delete newFeedbackMap[conversationId];
              return { feedbackMap: newFeedbackMap };
            },
            false,
            'feedback/clearConversationFeedback'
          );
        },

        clearState: () => set(initialState, false, 'feedback/clearState'),
      }),
      {
        name: 'feedback-store',
        partialize: state => ({ feedbackMap: state.feedbackMap }),
      }
    )
  )
);
